import alertService from '@jetbrains/ring-ui/components/alert-service/alert-service'
import type {FetchBaseQueryError} from '@reduxjs/toolkit/query'
import {useCallback, useEffect, useState} from 'react'
import {useNavigate} from 'react-router-dom'

import Routes, {getHref} from '../../../../../routes'
import {pipelinesApi} from '../../services/pipelinesApi'
import type {HTTPError} from '../../types'
import {instanceOf} from '../../types'
import {capitalizeFirstLetter} from '../../utils/string'
import {connectionId} from '../CreatePipelinePage.constants'

import {defaultPipelineJobAgent, defaultPipelineJobName} from './useCreatePipeline.consts'

const GENERATOR_MAX_ITERATIONS = 1000

function getPipelineNameByRepositoryName(name: string): string {
  return name.split(/[-_]/g).map(capitalizeFirstLetter).join(' ')
}

function generateName(repositoryName: string, pipelineNames: string[]): string {
  const name = getPipelineNameByRepositoryName(repositoryName)
  let suffix = 0
  let nameWithSuffix = name

  while (pipelineNames.includes(nameWithSuffix)) {
    suffix++
    if (suffix > GENERATOR_MAX_ITERATIONS) {
      throw new Error(`Error while generating new pipeline name`)
    }
    nameWithSuffix = `${name} ${suffix}`
  }

  return nameWithSuffix
}

async function processError(error: unknown) {
  let errorText = (error instanceof Error && error.message) || 'Create new pipeline failed'
  if (instanceOf<HTTPError>(error, 'statusCode') && error.response != null) {
    const responseText = (await error.response.text()).trim()
    if (responseText.length > 0) {
      errorText = responseText
    }
  } else if (instanceOf<FetchBaseQueryError>(error, 'status')) {
    errorText = String(error.data)
  }
  alertService.error(errorText)
  throw error
}

export default function useCreatePipeline(): {
  createPipeline: (
    repositoryUrl: string,
    repositoryName: string,
    branch: string,
    isPrivate: boolean,
    username?: string,
  ) => void
  pipelineCreating: boolean
} {
  const [pipelineCreating, setPipelineCreating] = useState<boolean>(false)
  const [getAllPipelines] = pipelinesApi.endpoints.getAllPipelines.useLazyQuerySubscription()
  const [createNewPipeline, {error}] = pipelinesApi.endpoints.createNewPipeline.useMutation()

  useEffect(() => {
    if (error) {
      processError(error)
    }
  }, [error])
  const navigate = useNavigate()

  const createPipeline = useCallback(
    async (
      repositoryUrl: string,
      repositoryName: string,
      branch: string,
      isPrivate: boolean,
      username?: string,
    ) => {
      setPipelineCreating(true)
      try {
        const {data: pipelines} = await getAllPipelines()
        const pipelineNames = pipelines?.map(pipeline => pipeline.settings.name) ?? []
        const name = generateName(repositoryName, pipelineNames)
        const pipelineResult = await createNewPipeline({
          settings: {
            name,
            jobs: {
              [defaultPipelineJobName]: {
                steps: [],
                'runs-on': defaultPipelineJobAgent,
              },
            },
          },
          vcsRoot: {
            url: repositoryUrl,
            branch,
            vcsName: 'jetbrains.git',
            isPrivate,
            connectionId,
            username,
          },
          triggers: [{type: 'vcsTrigger', parameters: {}}],
        })
        if ('data' in pipelineResult) {
          navigate(
            `${getHref(Routes.EDIT_PIPELINE, {
              id: pipelineResult.data.id,
            })}?job=${defaultPipelineJobName}`,
          )
        }
      } catch (e) {
        await processError(e)
      } finally {
        setPipelineCreating(false)
      }
    },
    [getAllPipelines, createNewPipeline, navigate],
  )

  return {pipelineCreating, createPipeline}
}
